import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { addCase } from '../../../services/firebase';
import { CaseType, CaseFormData } from '../../../types';

interface AddCaseFormProps {
  clientId: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

const caseTypes: CaseType[] = [
  'Asylum',
  'Work Permit',
  'Naturalization',
  'Adjustment Green Card',
];

type FormInputs = Omit<CaseFormData, 'amountCharged'> & {
  amountCharged: number | '';
};

const AddCaseForm: React.FC<AddCaseFormProps> = ({ clientId, onSuccess, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>({
    defaultValues: {
      type: 'Asylum',
      receiptNumber: '',
      applicationDate: new Date().toISOString().split('T')[0],
      amountCharged: '',
    },
  });

  const onSubmit = async (data: FormInputs) => {
    try {
      const payload: CaseFormData = {
        ...data,
        amountCharged: Number(data.amountCharged) || 0,
      };
      await addCase(clientId, payload);
      onSuccess();
    } catch (error) {
      console.error('Error adding case:', error);
    }
  };

  const labelSx = {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: 'text.primary',
    mb: 1,
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box>
            <Typography component="label" htmlFor="case-type" sx={labelSx}>
              Case Type
            </Typography>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Case type is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="case-type"
                  select
                  fullWidth
                  error={!!errors.type}
                  helperText={errors.type?.message}
                >
                  {caseTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="case-receipt" sx={labelSx}>
              Receipt Number
            </Typography>
            <Controller
              name="receiptNumber"
              control={control}
              rules={{ required: 'Receipt number is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="case-receipt"
                  fullWidth
                  placeholder="ABC-123-456-789"
                  error={!!errors.receiptNumber}
                  helperText={errors.receiptNumber?.message}
                />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="case-application-date" sx={labelSx}>
              Application Date
            </Typography>
            <Controller
              name="applicationDate"
              control={control}
              rules={{ required: 'Application date is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="case-application-date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.applicationDate}
                  helperText={errors.applicationDate?.message}
                />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="case-amount" sx={labelSx}>
              Amount Charged
            </Typography>
            <Controller
              name="amountCharged"
              control={control}
              rules={{
                required: 'Amount charged is required',
                validate: (v) =>
                  v === '' || Number(v) >= 0 || 'Amount must be greater than or equal to 0',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="case-amount"
                  type="number"
                  fullWidth
                  placeholder="0.00"
                  inputProps={{ step: '0.01', min: 0 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  error={!!errors.amountCharged}
                  helperText={errors.amountCharged?.message}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 1,
            }}
          >
            {onCancel && (
              <Button
                type="button"
                variant="text"
                onClick={onCancel}
                disabled={isSubmitting}
                sx={{ minHeight: 40 }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minHeight: 40, minWidth: 140 }}
              startIcon={
                isSubmitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
            >
              {isSubmitting ? 'Adding…' : 'Add Case'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default AddCaseForm;
